import type {
  RenderMediaKind,
  SyncMutation,
  Task,
  TaskListItem,
  TaskRender,
} from "@relais/shared";

export type SignedUploadHandle = {
  client_id: string;
  storage_path: string;
  signed_url: string;
  token: string;
};

export type MediaUploadProgress = {
  uploaded: number;
  total: number;
};

export type PendingMedia = {
  id: string;
  clientId: string;
  taskId: string;
  kind: RenderMediaKind;
  mimeType: string;
  sizeBytes: number;
  caption?: string;
};

export type FlushRejection = {
  kind: SyncMutation["kind"];
  task_id: string;
  status: "conflict" | "error";
  reason: string;
  code?: string;
};

export type FlushResult = {
  processed: number;
  failed: number;
  conflicts: number;
  completed: string[]; // mutation ids
  conflictTasks: Task[];
  rejections: FlushRejection[];
};

export type FlushOptions = {
  signal?: AbortSignal;
  limit?: number; // default 50
};

export type SyncTransport = {
  flush(
    mutations: SyncMutation[],
  ): Promise<{
    results: Array<
      | { status: "ok"; client_mutation_id: string; task_id: string; task: Task; render?: TaskRender }
      | { status: "conflict"; client_mutation_id: string; task_id: string; reason: string; server_task: Task; server_render?: TaskRender }
      | { status: "error"; client_mutation_id: string; task_id: string; code: string; message: string }
    >;
  }>;
  // Renvoie des TaskListItem (référent/assignee minimaux joints) : le référent
  // est mis en cache hors-ligne par OfflineSyncService.upsertTasks.
  fetchTasks(since?: string): Promise<TaskListItem[]>;
  createSignedUpload(
    taskId: string,
    clientId: string,
    ext: string,
  ): Promise<SignedUploadHandle>;
  uploadBlob(signedUrl: string, blob: Blob, mimeType: string): Promise<void>;
  fetchCatalogue(name: string): Promise<{
    name: string;
    version: string;
    fetchedAt: string;
    options: unknown[];
  }>;
};

// Ordre de priorité des mutations. Plus petit = traité en premier.
export const MUTATION_PRIORITY: Record<SyncMutation["kind"], number> = {
  accept: 10,
  refuse: 10,
  start: 20,
  resume: 20,
  update_render: 30,
  register_media: 40,
  remove_media: 40,
  submit: 50,
};

export const MAX_RETRY = 3;

export const MAX_UPLOAD_RETRY = 5;

// Une ligne processing plus vieille que ce seuil est considérée orpheline
// (flush interrompu : app tuée, timeout Dexie dans markCompleted) et repasse
// pending. Marge large : un flush légitime en vol dans un autre contexte JS
// (le lock est in-memory, pas multi-onglets) dure quelques secondes.
export const STALE_PROCESSING_MS = 5 * 60_000;

// Exponential backoff: 2s, 8s, 32s, 2m, 8m
export function uploadBackoffMs(retryCount: number): number {
  return Math.min(2_000 * Math.pow(4, retryCount - 1), 8 * 60_000);
}

export function inUploadBackoff(
  m: { uploadRetryCount?: number; uploadLastAttemptAt?: string },
  now: number = Date.now(),
): boolean {
  const rc = m.uploadRetryCount ?? 0;
  if (rc === 0 || !m.uploadLastAttemptAt) return false;
  const last = Date.parse(m.uploadLastAttemptAt);
  return now - last < uploadBackoffMs(rc);
}

export type UploadError = {
  taskId: string;
  mediaId: string;
  error: string;
};

export type UploadSweepResult = {
  uploaded: string[];
  errored: UploadError[];
};
